import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage({ onLogin, onBack }) {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? `${API}/auth/login` : `${API}/auth/register`;
      const response = await axios.post(endpoint, formData);
      toast.success(isLogin ? t("login_success") : t("register_success"));
      onLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("auth_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center bg-slate-50 px-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              {onBack && (
                <button onClick={onBack} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="back-to-landing">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
              )}
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Athlo Nexus</h1>
            </div>
            <p className="text-slate-600">{t("app_tagline")}</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isLogin ? t("login_title") : t("register_title")}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name" data-testid="register-name-label">{t("name")}</Label>
                  <Input id="name" type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required={!isLogin} data-testid="register-name-input" className="mt-1" />
                </div>
              )}
              <div>
                <Label htmlFor="email" data-testid="login-email-label">{t("email")}</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="login-email-input" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="password" data-testid="login-password-label">{t("password")}</Label>
                <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="login-password-input" className="mt-1" />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" disabled={loading} data-testid={isLogin ? "login-submit-button" : "register-submit-button"}>
                {loading ? t("loading") : isLogin ? t("login_btn") : t("register_btn")}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button onClick={() => setIsLogin(!isLogin)} className="text-sm text-slate-600 hover:text-slate-900" data-testid="toggle-auth-mode-button">
                {isLogin ? t("no_account") : t("have_account")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1757924284732-4189190321cf?crop=entropy&cs=srgb&fm=jpg&q=85')` }}>
        <div className="w-full h-full bg-slate-900/40 flex items-center justify-center p-12">
          <div className="text-white max-w-lg">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("app_hero")}</h2>
            <p className="text-lg text-slate-200">{t("app_hero_sub")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
